<template>
    <DCard
        :title="__('products.download_upload.plans.history.title')"
        padding
    >
        <DTable
            :key="tableKey"
            :items="items"
            :fields="columns"
        >
            <template #date="{item}">
                <td> {{ setDateFormat(item.created_at) }} </td>
            </template>
            <template #user="{item}">
                <td v-if="item.user?.display_name">
                    {{ item.user.display_name }} ({{ item.user.name }})
                </td>
                <td v-else>
                    {{ item.user?.name ?? '' }}
                </td>
            </template>
            <template #status="{item}">
                <td>
                    <span :class="`badge bg-${statusColor(item.status)}`">
                        {{ item.status }}
                    </span>
                </td>
            </template>
            <template #actions="{item}">
                <td>
                    <DDropdown
                        :class="'more-actions-button'"
                        :caret="false"
                        icon="dots-horizontal"
                    >
                        <DDropdownItem @click="download(item.id)">
                            {{ __('products.download_upload.plans.history.download') }}
                        </DDropdownItem>
                        <DDropdownItem
                            v-if="item.backup_file_path"
                            @click="downloadBackup(item.id)"
                        >
                            {{ __('products.download_upload.plans.history.download_backup') }}
                        </DDropdownItem>
                        <DDropdownItem
                            v-if="item.status === 'failed'"
                            @click="navigateToRetry(item.id)"
                        >
                            {{ __('products.download_upload.plans.history.view_errors_and_retry') }}
                        </DDropdownItem>
                    </DDropdown>
                </td>
            </template>
        </DTable>
        <DPagination
            v-if="imports.last_page >= 1"
            :active-page="imports.current_page"
            :pages="imports.last_page"
            align="center"
            @update:activePage="changePage"
        />
    </DCard>
</template>

<script setup>
import { __ } from '~@/Composables/translation'
import { route } from '~@/Composables/route'
import { router, usePage } from '@inertiajs/vue3'
import { ref, watch, onMounted, onBeforeUnmount } from 'vue'

const props = defineProps({
    imports: {
        type: Object,
        required: true,
    },
    indexRoute: {
        type: String,
        required: true,
    },
    downloadRoute: {
        type: String,
        required: true,
    },
    backupDownloadRoute: {
        type: String,
        required: true,
    },
    retryRoute: {
        type: String,
        required: true,
    },
    type: {
        type: String,
        required: true,
    },
})

const items = ref([...props.imports.data])
const tableKey = ref(0)

watch(() => props.imports.data, (newData) => {
    items.value = [...newData]
    tableKey.value++
})

const columns = [
    { key: 'id', label: 'Id' },
    { key: 'name', label: __('products.download_upload.plans.history.name') },
    { key: 'date', label: __('products.download_upload.plans.history.date') },
    { key: 'user', label: __('products.download_upload.plans.history.user') },
    { key: 'status', label: __('products.download_upload.plans.history.status') },
    { key: 'actions', label: '' },
]

const download = (id) => {
    window.location.href = route(props.downloadRoute, id)
}

const downloadBackup = (id) => {
    window.location.href = route(props.backupDownloadRoute, id)
}

const navigateToRetry = (id) => {
    router.get(route(props.retryRoute, id))
}

const changePage = (page) => {
    const query = {}

    if (page > 1) {
        query.page = page
    }

    router.get(route(props.indexRoute), query)
}

const statusColor = (status) => {
    const colors = {
        pending: 'warning',
        failed: 'danger',
        imported: 'success',
    }

    return colors[status] ?? 'secondary'
}

const handleStatusUpdate = (socketEvent) => {
    // Ignore events from other tenants and other import types (product vs addon share the channel).
    if (socketEvent.tenant !== usePage().props.tenant) {
        return
    }

    if (socketEvent.import.type !== props.type) {
        return
    }

    const index = items.value.findIndex(row => row.id === socketEvent.import.id)

    if (index === -1) {
        return
    }

    const previous = items.value[index]
    items.value.splice(index, 1, { ...previous, ...socketEvent.import })
    tableKey.value++
}

let channel = null

onMounted(() => {
    channel = window.laravelEcho.channel('onecrm.product_plans.import')
    channel.listen('.product_plans.import_updated', handleStatusUpdate)

    router.reload({ only: ['imports'] })
})

onBeforeUnmount(() => {
    if (channel) {
        window.laravelEcho.leave('onecrm.product_plans.import')
    }
})
</script>
